/*
 * Damekii collection page helpers. Both are custom elements so they re-initialise on their own
 * when Dawn's facets.js swaps the section HTML after a filter or sort change.
 *
 * <dk-load-more>   Infinite scroll: fetches the next page of the section and appends its cards.
 *                  The inner <a> is a real link, so without JS it still goes to the next page.
 * <dk-grid-toggle> Mobile 1 / 2 column switch, remembered per browser.
 */

if (!customElements.get('dk-load-more')) {
  customElements.define(
    'dk-load-more',
    class DkLoadMore extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-dk-more-btn]');
        this.grid = document.getElementById('product-grid');
        this.loading = false;
        if (!this.button || !this.grid) return;

        this.button.addEventListener('click', (e) => {
          e.preventDefault();
          this.load();
        });

        // Auto-load when the button nears the viewport, like an endless feed.
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting) this.load();
            },
            { rootMargin: '0px 0px 600px 0px' }
          );
          this.observer.observe(this.button);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        const next = this.dataset.next;
        if (this.loading || !next) return;
        this.loading = true;
        this.classList.add('is-loading');

        try {
          const url = next + (next.includes('?') ? '&' : '?') + 'section_id=' + this.dataset.sectionId;
          const html = await fetch(url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');

          doc.querySelectorAll('#product-grid > li').forEach((li) => {
            // New cards were never seen by Dawn's reveal observer; show them straight away.
            li.classList.remove('scroll-trigger', 'animate--slide-in');
            li.classList.add('dk-appended');
            this.grid.appendChild(li);
          });

          const incoming = doc.querySelector('dk-load-more');
          const shown = this.grid.children.length;
          const total = parseInt(this.dataset.total, 10) || shown;
          this.querySelector('[data-dk-shown]').textContent = shown;
          this.querySelector('[data-dk-bar]').style.width = Math.min(100, (shown / total) * 100) + '%';

          if (incoming && incoming.dataset.next) {
            this.dataset.next = incoming.dataset.next;
            this.button.href = incoming.dataset.next;
          } else {
            this.dataset.next = '';
            this.button.remove();
            if (this.observer) this.observer.disconnect();
          }
        } catch (err) {
          // Fall back to a normal page load if the fetch fails.
          window.location.href = next;
        } finally {
          this.loading = false;
          this.classList.remove('is-loading');
        }
      }
    }
  );
}

if (!customElements.get('dk-grid-toggle')) {
  customElements.define(
    'dk-grid-toggle',
    class DkGridToggle extends HTMLElement {
      connectedCallback() {
        this.buttons = this.querySelectorAll('[data-cols]');
        let saved = null;
        try {
          saved = localStorage.getItem('dk-grid-cols');
        } catch (e) {}
        this.apply(saved || this.dataset.default || '2');
        this.buttons.forEach((b) => b.addEventListener('click', () => this.apply(b.dataset.cols, true)));
      }

      apply(cols, save) {
        const grid = document.getElementById('product-grid');
        if (!grid) return;
        grid.classList.remove('grid--1-col-tablet-down', 'grid--2-col-tablet-down');
        grid.classList.add('grid--' + cols + '-col-tablet-down');
        this.buttons.forEach((b) => b.setAttribute('aria-pressed', b.dataset.cols === cols ? 'true' : 'false'));
        if (save) {
          try {
            localStorage.setItem('dk-grid-cols', cols);
          } catch (e) {}
        }
      }
    }
  );
}
